import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {makeStyles} from "@material-ui/core/styles";
import {StyledInviteUserItem} from "../../../shared/user/InviteUserItem";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import CSVImport from "../../../../_shared/CSVImport";
import {useNotification} from "../../../../ui/notification/NotificationProvider";
import * as _ from "lodash";
import {emailRegex, rand} from "../../../../../utils/function-util";
import {KeyValue, UserItem, UserRowData} from "../../../../../models/shared";
import {NavigationProps, WizardNavigation} from "../../../../_shared/wizard/WizardNavigation";

const useStyles = makeStyles(theme => ({
    actions: {
        '& > *': {
            marginRight: theme.spacing(2),
        },
    },
    addBtn: {
        marginTop: theme.spacing(4)
    }
}));
const CSVFormat = {
    headers: ['Email', 'FirstName', 'LastName'],
    examples: [
        ['js.smith@gmail.com', 'John', 'Smith'],
        ['j.doe@gmail.com', 'James', 'Doe']
    ]
};
const buildItemData = (data: string[]): UserRowData => {
    if (!data || !data.length) {
        return {} as UserRowData;
    }
    return {
        email: data[0],
        firstName: data[1],
        lastName: data[2]
    };
};
const buildItem = (data = []): UserItem => ({
    id: rand(),
    data: buildItemData(data),
    errors: {}
});

interface Props extends NavigationProps {
    providersCount: number,
    onChangeGuestList: any
}

export const Step3Guests: React.FC<Props> = ({onChangeGuestList, providersCount, ...props}) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const [forms, setForms] = useState<any[]>([]);
    const [submitted, setSubmitted] = useState(false);
    const {pushNotification} = useNotification();

    const addHandler = () => {
        setForms(newForms => ([...newForms, {...buildItem()}]));
    };

    const resetHandler = async () => {
        setForms([]);
    };

    const updateForm = (id: any, patch: any) => {
        setForms(newForms => {
            const index = newForms.findIndex(f => f.id === id);
            if (index <= -1)
                return newForms;
            const result = [...newForms];
            result[index] = _.merge({}, newForms[index], patch);
            return result;
        });
    };

    const handleFormChange = (name: any, value: any, form: any) => {
        const errors = validateField(name, value);
        const update = {
            data: {[name]: value},
            errors: errors
        };
        updateForm(form.id, update);
    };

    const handleFormDeleted = (id: any) => {
        setForms(formsToDelete => {
            const index = formsToDelete.findIndex(f => f.id === id);
            if (index <= -1)
                return formsToDelete;
            const newForms = [...formsToDelete];
            newForms.splice(index, 1);
            return newForms;
        });
    };

    const validateField = (field: any, value: any) => {
        const errors: KeyValue<string> = {};
        if (field === 'email') {
            errors[field] = emailRegex.test(value) ? null : 'EmailIsInvalid';
        }
        else {
            errors[field] = !value ? 'FieldIsRequired' : null;
        }
        return errors;
    };

    const loadCSVData = (data: any) => {
        setForms(data.map((row: any) => buildItem(row)));
    };

    const onLoadCSVError = (errorMsg: any, errorCode: any) => {
        const errorCodeMsg = errorCode ? '\nError code: ' + errorCode : '';
        if (errorMsg) {
            pushNotification({
                message: errorMsg + errorCodeMsg,
                severity: 'warning'
            });
        }
    };

    const onSubmitChange = (changeTab: any) => {
        setSubmitted(true);
        let validatedForms = [];
        validatedForms = forms.map(f => {
            const errors = ['email', 'firstName', 'lastName'].reduce((acc, name) => ({...acc, ...validateField(name, f.data[name])}), {});
            return {
                ...f,
                errors
            };
        });
        if (validatedForms.length && validatedForms.some(f => _.values(f.errors).some((v: any) => !!v)))
            return setForms(validatedForms);
        onChangeGuestList(forms.filter(g => g.data.email));
        changeTab();
    };

    return <Grid container spacing={3}>
        <Grid item xs={12}>
            <Typography variant="body1">{t('ProGuest.Groups.Tabs.Guests.Description')}</Typography>
        </Grid>
        <Grid item xs={12}>
            {forms.length > 0 ?
                forms.map((f, i) => <StyledInviteUserItem form={f} key={f.id.toString()}
                                                    handleFormDeleted={handleFormDeleted}
                                                    handleFormChange={handleFormChange} submitted={submitted}>
                </StyledInviteUserItem>) :
                <Typography variant="body2" align="center">
                    {t('ProGuest.Groups.Tabs.Guests.AddGuests')}
                </Typography>}
            <Grid container justify="flex-start">
                <IconButton aria-label="add" className={classes.addBtn} onClick={addHandler}>
                    <AddIcon/>
                </IconButton>
            </Grid>
            <Grid container justify="flex-end" className={classes.actions}>
                <Button variant="outlined" color="primary" size="medium" onClick={resetHandler}>
                    {t('_Common.Labels.Reset')}
                </Button>
            </Grid>
            <CSVImport displayedLabel={t('_Common.Labels.CSV')} format={CSVFormat} onComplete={loadCSVData}
                       onError={onLoadCSVError}/>
        </Grid>
        <WizardNavigation {...props} onSubmitChange={onSubmitChange}/>
    </Grid>;
};
